// src/utils/dashboardSituation.js
import { api } from "../services/api";
/**
 * Muuntaa Date-arvon YYYY-MM-DD-muotoon.
 */
function toDashboardYmd(date) {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
/**
 * Muotoilee YYYY-MM-DD-päivän suomalaiseksi päivämääräksi.
 * Esim. 2026-09-30 -> 30.9.2026
 */
function formatDashboardDateFi(ymd) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(
    String(ymd || "")
  );
  if (!match) {
    return String(ymd || "");
  }
  const [, year, month, day] = match;
  return `${Number(day)}.${Number(month)}.${year}`;
}
/**
 * Laskee kalenterivastauksesta aktiiviset varauspyynnöt.
 */
function countPendingCalendarRequests(calendarData) {
  const events = Array.isArray(calendarData?.events)
    ? calendarData.events
    : [];
  return events.reduce((total, event) => {
    const status = String(
      event?.status ??
      event?.extendedProps?.status ??
      ""
    ).toLowerCase();
    // Aikakohtaisessa kalenterissa päivä voi olla edelleen avoin,
    // vaikka sillä olisi aktiivisia tarjouspyyntöjä.
    const timedPendingCount = Number(
      event?.extendedProps?.pending_count ?? 0
    );
    if (
      Number.isFinite(timedPendingCount) &&
      timedPendingCount > 0
    ) {
      return total + timedPendingCount;
    }
    // Päiväkohtaisessa kalenterissa aktiivinen hold näkyy
    // pending-eventtinä.
    if (status === "pending") {
      return total + 1;
    }
    return total;
  }, 0);
}
/**
 * Palauttaa kalenterista aidosti avoimet tulevat päivät.
 *
 * Backend palauttaa avoimen päivän eventtinä:
 * {
 *   start: "YYYY-MM-DD",
 *   status / extendedProps.status: "open"
 * }
 *
 * Päivät järjestetään aikajärjestykseen.
 */
function getOpenCalendarDays(calendarData, todayYmd) {
  const events = Array.isArray(calendarData?.events)
    ? calendarData.events
    : [];
  const days = events
    .filter((event) => {
      const status = String(
        event?.status ??
        event?.extendedProps?.status ??
        ""
      ).toLowerCase();
      return status === "open";
    })
    .map((event) => {
      const rawDay =
        event?.start ??
        event?.date ??
        event?.ymd ??
        "";
      return String(rawDay).slice(0, 10);
    })
    .filter(
      (day) =>
        /^\d{4}-\d{2}-\d{2}$/.test(day) &&
        day >= todayYmd
    );
  return [...new Set(days)].sort();
}
function isDashboardRepertoireType(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
  return [
    "bilebandi",
    "bandi",
    "band",
    "bandiomamusiikki",
    "trubaduuri",
    "duo",
    "sooloartisti",
  ].includes(normalized);
}
/**
 * Rakentaa kirjautuneen käyttäjän Dashboard-huomiot.
 *
 * Prioriteetit:
 * 1 = aktiivinen varauspyyntö
 * 2 = kalenteri
 * 3 = tekstiviesti-ilmoitukset
 * 4 = ohjelmisto puuttuu
 * 5 = UKK puuttuu
 */
export async function buildDashboardSituationItems(
  sourceBands = []
) {
  const ownerBands = (
    Array.isArray(sourceBands) ? sourceBands : []
  ).filter(
    (item) =>
      String(item?.role || "").toLowerCase() === "owner"
  );
  if (ownerBands.length === 0) {
    return [];
  }
  // Tarkistetaan tulevat tapahtumat kahden vuoden päähän.
  const todayDate = new Date();
  const today = toDashboardYmd(todayDate);
  const fromDate = new Date(todayDate);
  fromDate.setDate(fromDate.getDate() - 1);
  const toDate = new Date(todayDate);
  toDate.setFullYear(toDate.getFullYear() + 2);
  // Jos viimeinen vapaa päivä on enintään 90 päivän päässä,
  // muistutetaan avaamaan kalenteria pidemmälle.
  const calendarReminderDate = new Date(todayDate);
  calendarReminderDate.setDate(
    calendarReminderDate.getDate() + 90
  );
  const from = toDashboardYmd(fromDate);
  const to = toDashboardYmd(toDate);
  const calendarReminderTo =
    toDashboardYmd(calendarReminderDate);
  const taskGroups = await Promise.all(
    ownerBands.map(async (summaryBand) => {
      const rawBandId =
        summaryBand?.band_id ??
        summaryBand?.id;
      const bandId = String(rawBandId ?? "").trim();
      if (!bandId) {
        return [];
      }
      // Haetaan rinnakkain profiilin tiedot ja kalenteri.
      const [detailResult, calendarResult] =
        await Promise.allSettled([
          api.bandByIdOrSlug(bandId),
          api.calendar(bandId, { from, to }),
        ]);
      const fullBand =
        detailResult.status === "fulfilled"
          ? (
              detailResult.value?.band ??
              detailResult.value ??
              null
            )
          : null;
      const calendarData =
        calendarResult.status === "fulfilled"
          ? calendarResult.value
          : null;
      const bandName =
        fullBand?.name ??
        fullBand?.band_name ??
        summaryBand?.name ??
        summaryBand?.band_name ??
        `Profiili #${bandId}`;
      const tasks = [];
      // --------------------------------------------------------
      // 1) AKTIIVINEN VARAUSPYYNTÖ
      // --------------------------------------------------------
      if (calendarData) {
        const pendingCount =
          countPendingCalendarRequests(calendarData);
        if (pendingCount > 0) {
          tasks.push({
            key: `${bandId}:booking`,
            type: "booking",
            priority: 1,
            bandId,
            bandName,
            count: pendingCount,
            title:
              pendingCount === 1
                ? "1 aktiivinen varauspyyntö"
                : `${pendingCount} aktiivista varauspyyntöä`,
            description:
              pendingCount === 1
                ? "Vastaa pyyntöön sähköpostiisi lähetetystä linkistä ennen määräaikaa."
                : "Vastaa pyyntöihin sähköpostiisi lähetetyistä linkeistä ennen määräaikaa.",
          });
        }
        // ------------------------------------------------------
        // 2) KALENTERI
        // ------------------------------------------------------
        const openDays =
          getOpenCalendarDays(calendarData, today);
        // Ei yhtään aidosti varattavaa päivää.
        if (openDays.length === 0) {
          tasks.push({
            key: `${bandId}:calendar`,
            type: "calendar",
            priority: 2,
            bandId,
            bandName,
            tab: "calendar",
            title: "Kalenterissa ei ole vapaita päiviä",
            description:
              "Asiakas voi silti lähettää saatavuuskyselyn viestillä. Päivämäärään sidottu tarjouspyyntö edellyttää, että kalenterissa on vapaita päiviä.",
            actionLabel: "Avaa kalenteri",
          });
        } else {
          const lastOpenDay =
            openDays[openDays.length - 1];
          // Kalenteri loppuu 90 päivän sisällä.
          if (lastOpenDay <= calendarReminderTo) {
            tasks.push({
              key: `${bandId}:calendar`,
              type: "calendar",
              priority: 2,
              bandId,
              bandName,
              tab: "calendar",
              title: "Kalenteri kaipaa päivitystä",
              description:
                `Viimeinen avoin päiväsi on ${formatDashboardDateFi(
                  lastOpenDay
                )}. ` +
                "Asiakas voi edelleen lähettää sinulle viestin profiilisi yhteydenottolomakkeella. " +
                "Avaa lisää päiviä myös myöhempiä varaustiedusteluja varten.",
              actionLabel: "Avaa kalenteri",
            });
          }
        }
      }
      // Jos profiilin haku epäonnistui, ei päätellä ALV:ta
      // tai UKK:ta puuttuvaksi vajaan datan perusteella.
      if (!fullBand) {
        return tasks;
      }
     // --------------------------------------------------------
      // 3) TEKSTIVIESTI-ILMOITUKSET
      // --------------------------------------------------------
      const hasBandPhone =
        String(fullBand?.band_phone || "").trim().length > 0;
      const rawSmsEnabled =
        fullBand?.sms_notifications_enabled ??
        fullBand?.settings?.sms_notifications_enabled ??
        false;
      const smsEnabled =
        rawSmsEnabled === true ||
        rawSmsEnabled === 1 ||
        rawSmsEnabled === "1" ||
        rawSmsEnabled === "true";
      if (hasBandPhone && !smsEnabled) {
        tasks.push({
          key: `${bandId}:sms`,
          type: "sms",
          priority: 3,
          bandId,
          bandName,
          tab: "calendar",
          title: "Tekstiviesti-ilmoitukset eivät ole käytössä",
          description:
            "Saat uudesta tarjouspyynnöstä halutessasi ilmoituksen myös tekstiviestillä.",
          actionLabel: "Ota käyttöön",
        });
      }
      // --------------------------------------------------------
      // 4) OHJELMISTO PUUTTUU
      // --------------------------------------------------------
      const artistType =
        fullBand?.artist_type ??
        fullBand?.artist_type_name ??
        "";
      // Ohjelmisto kuuluu vain musiikkiprofiileille.
      // Huomio näytetään vain, jos backend palauttaa repertoire-taulukon
      // ja siinä ei ole yhtään kelvollista artisti + kappale -riviä.
      const repertoireItems = Array.isArray(fullBand?.repertoire)
        ? fullBand.repertoire.filter(
            (item) =>
              String(item?.artist || "").trim() &&
              String(item?.title || "").trim()
          )
        : null;
      if (
        isDashboardRepertoireType(artistType) &&
        Array.isArray(repertoireItems) &&
        repertoireItems.length === 0
      ) {
        tasks.push({
          key: `${bandId}:repertoire`,
          type: "repertoire",
          priority: 4,
          bandId,
          bandName,
          tab: "repertoire",
          title: "Ohjelmisto puuttuu",
          description:
            "Lisää ohjelmistoon kappaleita, jotta asiakas näkee millaista musiikkia esität ja löytää profiilistasi tuttuja biisejä.",
          actionLabel: "Lisää ohjelmisto",
        });
      }
      // --------------------------------------------------------
      // 5) UKK PUUTTUU
      // --------------------------------------------------------
      const faqItems = Array.isArray(fullBand?.faq_items)
        ? fullBand.faq_items
        : [];
      if (faqItems.length === 0) {
        tasks.push({
          key: `${bandId}:faq`,
          type: "faq",
          priority: 5,
          bandId,
          bandName,
          tab: "faq",
          title: "UKK-osio on tyhjä",
          description:
            "Hyvä UKK auttaa asiakasta ja parantaa profiilin löydettävyyttä hakukoneissa.",
          actionLabel: "Täytä UKK",
        });
      }
      return tasks;
    })
  );
  return taskGroups
    .flat()
    .sort((a, b) => {
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
      return String(a.bandName || "").localeCompare(
        String(b.bandName || ""),
        "fi"
      );
    });
}
/**
 * Tekee samasta datasta yhteenvedon Dashboardin modalia
 * ja etusivun pientä statusmerkkiä varten.
 */
export function summarizeDashboardSituation(items = []) {
  const safeItems = Array.isArray(items) ? items : [];
  const bookingCount = safeItems
    .filter((item) => item?.type === "booking")
    .reduce(
      (sum, item) =>
        sum + Math.max(0, Number(item?.count || 0)),
      0
    );
  const profileNoticeCount = safeItems.filter(
    (item) => item?.type !== "booking"
  ).length;
  const bookingText =
    bookingCount === 1
      ? "1 aktiivinen varauspyyntö"
      : `${bookingCount} aktiivista varauspyyntöä`;
  const profileText =
    profileNoticeCount === 1
      ? "1 profiilihuomio"
      : `${profileNoticeCount} profiilihuomiota`;
  const text =
    bookingCount > 0 && profileNoticeCount > 0
      ? `Sinulla on ${bookingText} ja ${profileText}.`
      : bookingCount > 0
        ? `Sinulla on ${bookingText}.`
        : profileNoticeCount > 0
          ? `Sinulla on ${profileText}.`
          : "";
  return {
    bookingCount,
    profileNoticeCount,
    totalCount: bookingCount + profileNoticeCount,
    text,
  };
}